import images from '@assets/images/index'
import { Spin } from 'antd'
import { memo, useMemo } from 'react'
import { Cluster, Strand } from 'src/types/type'

import { handleEnterOrSpacePress } from '../LearningCenter/util'

interface ClusterDetailsProps {
  selectedCluster: Cluster
  selectedGrade: string | string[]
  checkedModulesAndClusters: {
    [key: string]: boolean
  }
  handleCheckboxChange: (moduleName: string) => void
  getModuleContent: (moduleSlug: string) => JSX.Element
}

function combineStrands(strands: Strand[]): Strand[] {
  const combined: { [key: string]: Set<string> } = {}

  strands.forEach((strand) => {
    const { name, fimModules } = strand
    if (!combined[name]) {
      combined[name] = new Set()
    }
    const fimModulesArray =
      typeof fimModules === 'string' ? fimModules.split('\n') : fimModules
    fimModulesArray.forEach((module) => combined[name].add(module.trim()))
  })

  return Object.entries(combined).map(([name, fimModulesSet]) => ({
    name,
    fimModules: Array.from(fimModulesSet).join('\n')
  }))
}

const ClusterDetails = memo(
  ({
    selectedCluster,
    selectedGrade,
    checkedModulesAndClusters,
    handleCheckboxChange,
    getModuleContent
  }: ClusterDetailsProps) => {
    // Memoize the combined strands to avoid unnecessary recomputation
    const combinedStrands = useMemo(
      () => combineStrands([...selectedCluster.strands]),
      [selectedCluster.strands]
    )
    // Memoize the conversion of combined strands
    const convertedStrands = useMemo(
      () =>
        combinedStrands.map((item) => ({
          ...item,
          fimModules:
            typeof item.fimModules === 'string'
              ? item.fimModules.split('\n')
              : item.fimModules
        })),
      [combinedStrands]
    )
    // Memoize the conversion of the selectedCluster with the converted strands
    const convertedSelectedCluster = useMemo(
      () => ({
        ...selectedCluster,
        strands: convertedStrands
      }),
      [selectedCluster, convertedStrands]
    )
    return (
      <div className="correlation-card">
        {convertedSelectedCluster ? (
          <>
            <h4 className="correlation-card__heading fw-bold leading-1">
              Grade {selectedGrade}
            </h4>
            <div className="correlation-card__content">
              <h5 className="correlation-card__content-sub-heading leading-1 fw-bold">
                {convertedSelectedCluster.name}
              </h5>
              {convertedSelectedCluster.strands.length > 0 ? (
                convertedSelectedCluster.strands.map(({ name, fimModules }) => (
                  <ul className="sub-card" key={name}>
                    <li className="sub-card-item">
                      <h5 className="sub-card-item__title fw-bold leading-1">
                        {name}
                      </h5>
                      <ul className="sub-card-item__checkbox-group">
                        {Array.from(new Set(fimModules))
                          .filter((module) => module.length > 0)
                          .map((module) => (
                            <li
                              className="sub-card-item__checkbox-group-item"
                              key={module}
                            >
                              <div className="item__chkbox checkbox-item">
                                <div
                                  className="pretty p-icon p-toggle"
                                  aria-checked={
                                    checkedModulesAndClusters[
                                      `${selectedGrade}-${name}-${module}`
                                    ] || false
                                  }
                                  aria-label={`Toggle selection for module ${module} in strand ${name}`}
                                  tabIndex={0}
                                  role="checkbox"
                                  onKeyDown={(e) =>
                                    handleEnterOrSpacePress(e, () =>
                                      handleCheckboxChange(
                                        `${selectedGrade}-${name}-${module}`
                                      )
                                    )
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    checked={
                                      checkedModulesAndClusters[
                                        `${selectedGrade}-${name}-${module}`
                                      ] || false
                                    }
                                    onChange={() =>
                                      handleCheckboxChange(
                                        `${selectedGrade}-${name}-${module}`
                                      )
                                    }
                                  />
                                  <div className="state p-on">
                                    <images.CheckboxCheckedLg />
                                  </div>
                                  <div className="state p-off">
                                    <images.CheckboxEmptyLg />
                                  </div>
                                </div>
                              </div>
                              <div className="item__label">
                                {getModuleContent(module)}
                              </div>
                            </li>
                          ))}
                      </ul>
                    </li>
                  </ul>
                ))
              ) : (
                <Spin />
              )}
            </div>
          </>
        ) : (
          <p>Select a cluster to see the details</p>
        )}
      </div>
    )
  }
)

// Display name for better debugging
ClusterDetails.displayName = 'ClusterDetails'

export default ClusterDetails
